<template>
  <div class="chat-input-wrap">
    <!-- @ 智能体选择器（绝对定位悬浮在输入卡片上方，不占文档流，避免挤压中间欢迎区） -->
    <div v-if="atPickerVisible" ref="atPickerRef" class="at-picker">
      <div class="at-picker-head">
        <span class="at-picker-title">选择智能体</span>
        <span class="at-picker-count">{{ atFilteredAgents.length }} 个</span>
        <button class="kb-picker-close at-picker-close" title="关闭" @click="closeAtPicker">&times;</button>
      </div>
      <div class="at-picker-search">
        <el-input
          v-model="atPickerKeyword"
          placeholder="搜索智能体…"
          size="small"
          clearable
          @input="atPickerIndex = 0"
          @keydown="handleAtSearchKeydown"
        />
      </div>
      <div class="at-picker-list">
        <template v-if="businessAgents.length > 0">
          <div class="at-picker-group-label business">业务智能体</div>
          <div
            v-for="(agent, i) in businessAgents"
            :key="agent.agentCode"
            class="at-picker-item business"
            :class="{ active: i === atPickerIndex }"
            @mousedown.prevent="confirmAtPick(agent)"
            @mouseenter="atPickerIndex = i"
          >
            <AgentAvatar :avatar="agent.agentAvatar" :name="agent.agentName" :size="28" />
            <div class="at-picker-info">
              <span class="at-picker-name">{{ agent.agentName }}</span>
              <span class="at-picker-desc">{{ agent.agentDesc || '暂无简介' }}</span>
            </div>
            <span class="at-picker-type business">业务</span>
            <span class="at-picker-kbcount">{{ agentKbCount(agent) }}个库</span>
          </div>
        </template>
        <template v-if="systemAgents.length > 0">
          <div v-if="businessAgents.length > 0" class="at-picker-divider"></div>
          <div class="at-picker-group-label system">系统智能体</div>
          <div
            v-for="(agent, i) in systemAgents"
            :key="agent.agentCode"
            class="at-picker-item system"
            :class="{ active: (businessAgents.length + i) === atPickerIndex }"
            @mousedown.prevent="confirmAtPick(agent)"
            @mouseenter="atPickerIndex = businessAgents.length + i"
          >
            <AgentAvatar :avatar="agent.agentAvatar" :name="agent.agentName" :size="28" />
            <div class="at-picker-info">
              <span class="at-picker-name">{{ agent.agentName }}</span>
              <span class="at-picker-desc">{{ agent.agentDesc || '暂无简介' }}</span>
            </div>
            <span class="at-picker-type system">系统</span>
            <span class="at-picker-kbcount">{{ agentKbCount(agent) }}个库</span>
          </div>
        </template>
        <div v-if="atFilteredAgents.length === 0" class="at-picker-empty">未找到匹配的智能体</div>
      </div>
      <div class="at-picker-footer">↑↓ 选择 &nbsp;Enter 确认 &nbsp;Esc 关闭</div>
    </div>

    <div class="chat-input-card">
    <!-- 智能体上下文栏：当前有效智能体 + 知识库入口（点击弹窗选择，默认全选） -->
    <div v-if="effectiveAgentObj" class="agent-context-bar">
      <div class="agent-chip-row">
        <span class="mention-agent-label">智能体</span>
        <div
          class="mention-agent-chip"
          :class="{ 'is-system': isSystemAgent(effectiveAgentObj) }"
          title="点击切换智能体"
          @click="openAtPickerFromChip"
        >
          <span class="mention-at">@</span>
          <AgentAvatar :avatar="effectiveAgentObj.agentAvatar" :name="effectiveAgentObj.agentName" :size="18" />
          <span class="mention-agent-name">{{ effectiveAgentObj.agentName }}</span>
          <span class="mention-agent-switch">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 2l4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14"/><path d="M7 22l-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/></svg>
            切换
          </span>
        </div>
        <!-- 知识库选择入口：点击弹出选择面板 -->
        <div
          v-if="effectiveKnowledges.length > 0"
          class="kb-chip"
          title="点击选择知识库"
          @click="openKbPicker"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
          <span class="kb-chip-status">{{ contextAll ? '全部' : `已选 ${contextKbs.length} 个` }}</span>
          <span class="kb-chip-switch">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 2l4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14"/><path d="M7 22l-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/></svg>
            选择
          </span>
        </div>
      </div>
      <!-- 知识库选择弹窗（浮层）：全部 + 逐个多选，选择后不自动关闭可连续操作 -->
      <div v-if="kbPickerVisible" class="kb-picker" @click.stop>
        <div class="kb-picker-head">
          <span class="kb-picker-title">选择知识库</span>
          <button class="kb-picker-close" title="关闭" @click="closeKbPicker">&times;</button>
        </div>
        <div class="kb-picker-list">
          <div
            class="kb-picker-item all"
            :class="{ active: contextAll }"
            @click="$emit('select-all-kbs')"
          >
            <span class="kb-picker-check" :class="{ active: contextAll }">
              <svg v-if="contextAll" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </span>
            <span class="kb-picker-name">全部</span>
            <span class="kb-picker-desc">使用当前智能体的全部知识库</span>
          </div>
          <div
            v-for="kb in effectiveKnowledges"
            :key="kb.id"
            class="kb-picker-item"
            :class="{ active: !contextAll && contextKbs.includes(Number(kb.id)) }"
            @click="$emit('toggle-kb', kb.id)"
          >
            <span class="kb-picker-check" :class="{ active: !contextAll && contextKbs.includes(Number(kb.id)) }">
              <svg v-if="!contextAll && contextKbs.includes(Number(kb.id))" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </span>
            <span class="kb-picker-name">{{ kb.knowledgeName }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 图片预览：作为独立行排在 textarea 之上，自身占高度，textarea 保持原始高度（参考豆包：有图卡片自然变高，无图保持默认） -->
    <div v-if="imagePreview" class="chat-input-image-preview">
      <div class="preview-thumb">
        <img :src="imagePreview" alt="preview" />
        <button class="image-remove-btn" @click="$emit('remove-image')" title="移除图片">&times;</button>
      </div>
    </div>

    <!-- Input body: textarea + counter + toolbar -->
    <div class="chat-input-body">
      <textarea
        ref="chatInputRef"
        :value="inputText"
        class="chat-input-textarea"
        :style="{
          paddingTop: '16px',
          minHeight: imagePreview ? '88px' : '52px'
        }"
        :placeholder="inputPlaceholder"
        :disabled="loading"
        maxlength="2048"
        rows="1"
        @keydown="handleInputKeydown"
        @input="handleInput"
      ></textarea>
      <div v-if="inputText.length > 0" class="chat-input-counter">{{ inputText.length }}/2048</div>
      <!-- Bottom toolbar -->
      <div class="chat-input-toolbar">
        <div class="chat-input-tools">
          <!-- 每日签到 + 积分余额：与提问强相关，放在工具区首位；签到跟人不跟页面，
               在官网/悬浮助手/本页任一处签过，这里当天都显示「已签到」 -->
          <AiSigninChip variant="chip" />
          <button
            class="chat-tool-chip"
            :class="{ active: thinkingEnabled }"
            @click="$emit('toggle-thinking')"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 2a7 7 0 0 1 7 7c0 2.5-1.3 4.7-3.2 6 .3.5.4 1.1.2 1.7-.2.6-.7 1-1.3 1.1-.8.2-1.6-.1-2.1-.7A7 7 0 0 1 12 2z"/>
              <path d="M9.5 14.5c-.3.8-.1 1.7.5 2.3l.1.1c.4.4.6 1 .4 1.6-.2.5-.6.9-1.1 1-.6.1-1.2-.1-1.6-.5"/>
              <path d="M14.5 14.5c.3.8.1 1.7-.5 2.3l-.1.1c-.4.4-.6 1-.4 1.6.2.5.6.9 1.1 1 .6.1 1.2-.1 1.6-.5"/>
            </svg>
            <span>深度思考</span>
          </button>
          <!-- 语音设置：按钮上方小弹窗（唤醒开关 + 唤醒词），常驻监听时显示红点 -->
          <div class="voice-setting-wrap">
            <button
              class="chat-tool-chip"
              :class="{ active: settingsVisible }"
              title="语音唤醒与语音输入设置"
              @click="$emit('update:settings-visible', !settingsVisible)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                <line x1="12" y1="19" x2="12" y2="23"/>
                <line x1="8" y1="23" x2="16" y2="23"/>
              </svg>
              <span>语音设置</span>
              <span v-if="kwsListening" class="tool-kws-dot" title="语音唤醒监听中"></span>
            </button>
            <div v-if="settingsVisible" class="voice-setting-pop">
              <div class="vsp-row">
                <div class="vsp-info">
                  <div class="vsp-label">语音唤醒</div>
                  <div class="vsp-desc">常驻监听唤醒词，唤醒后自动录音</div>
                </div>
                <el-switch
                  :model-value="kwsEnabled"
                  size="small"
                  @update:model-value="$emit('update:kws-enabled', $event)"
                />
              </div>
              <div class="vsp-row column">
                <div class="vsp-info">
                  <div class="vsp-label">唤醒词</div>
                  <div class="vsp-desc">只支持中文，如：你好虎鱼</div>
                </div>
                <el-input
                  :model-value="kwsKeywords"
                  placeholder="请输入唤醒词"
                  size="small"
                  maxlength="20"
                  @update:model-value="$emit('update:kws-keywords', $event)"
                />
              </div>
              <div class="vsp-row">
                <div class="vsp-info">
                  <div class="vsp-label">朗读引擎</div>
                  <div class="vsp-desc">本地离线速度快，在线音色更自然</div>
                </div>
                <el-radio-group
                  :model-value="ttsEngine"
                  size="small"
                  @update:model-value="$emit('update:tts-engine', $event)"
                >
                  <el-radio-button value="local">本地</el-radio-button>
                  <el-radio-button value="edgetts">在线</el-radio-button>
                </el-radio-group>
              </div>
              <div v-if="ttsEngine === 'edgetts'" class="vsp-row column">
                <div class="vsp-info">
                  <div class="vsp-label">朗读音色</div>
                  <div class="vsp-desc">在线引擎可选音色，支持中英混合</div>
                </div>
                <el-select
                  :model-value="ttsVoice"
                  placeholder="请选择音色"
                  size="small"
                  style="width: 100%"
                  @update:model-value="$emit('update:tts-voice', $event)"
                >
                  <el-option
                    v-for="v in edgeTtsVoices"
                    :key="v.id"
                    :label="v.name"
                    :value="v.id"
                  />
                </el-select>
              </div>
              <div class="vsp-footer">
                <el-button size="small" @click="$emit('update:settings-visible', false)">取消</el-button>
                <el-button size="small" type="primary" @click="$emit('save-kws')">保存</el-button>
              </div>
            </div>
            <!-- 小箭头放在弹窗外层，避免弹窗内部滚动时被裁剪 -->
            <span v-if="settingsVisible" class="vsp-arrow"></span>
          </div>
        </div>
        <div class="chat-input-actions">
          <input
            ref="imageInputRef"
            type="file"
            accept="image/*"
            style="display:none"
            @change="handleImageChange"
          />
          <button
            v-if="agentSupportsImage"
            class="chat-action-btn"
            title="上传图片"
            @click="imageInputRef.click()"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
          </button>
          <!-- 语音输入：点击直接开始收音，录音浮层以该按钮为锚点向上浮出 -->
          <div class="voice-input-wrap">
            <button
              class="chat-action-btn"
              title="语音输入"
              @click="$emit('voice-toggle')"
            >
              <el-icon><Microphone /></el-icon>
            </button>
            <VoiceFloatPanel
              :voice-active="voiceActive"
              :is-recording="isRecording"
              :partial-text="partialText"
              :record-seconds="recordSeconds"
              @cancel-record="$emit('cancel-record')"
              @finish="$emit('voice-finish')"
              @start-input="$emit('voice-start-input')"
            />
          </div>
          <button
            v-if="loading"
            class="chat-send-btn cancel"
            title="取消"
            @click="$emit('cancel')"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="6" width="12" height="12" rx="2"/>
            </svg>
          </button>
          <button
            v-else
            class="chat-send-btn"
            :disabled="!canSend"
            title="发送"
            @click="$emit('send')"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="19" x2="12" y2="5"/>
              <polyline points="5 12 12 5 19 12"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { Microphone } from '@element-plus/icons-vue'
import AgentAvatar from '@/modules/huyu-ai/backend/components/AgentAvatar/index.vue'
import AiSigninChip from '@/modules/huyu-framework/backend/components/AiSigninChip/index.vue'
import VoiceFloatPanel from './VoiceFloatPanel.vue'

const props = defineProps({
  agentList: { type: Array, default: () => [] },
  inputText: { type: String, default: '' },
  inputPlaceholder: { type: String, default: '发送消息...' },
  canSend: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  imagePreview: { type: String, default: '' },
  effectiveAgentObj: { type: Object, default: null },
  effectiveKnowledges: { type: Array, default: () => [] },
  contextAll: { type: Boolean, default: false },
  contextKbs: { type: Array, default: () => [] },
  mentionedAgent: { type: String, default: '' },
  thinkingEnabled: { type: Boolean, default: false },
  agentSupportsImage: { type: Boolean, default: false },
  agentKbCount: { type: Function, default: () => 0 },
  kwsListening: { type: Boolean, default: false },
  // 语音设置小弹窗（唤醒开关 + 唤醒词）
  settingsVisible: { type: Boolean, default: false },
  kwsEnabled: { type: Boolean, default: false },
  kwsKeywords: { type: String, default: '' },
  // 朗读引擎与音色
  ttsEngine: { type: String, default: 'local' },
  ttsVoice: { type: String, default: '' },
  edgeTtsVoices: { type: Array, default: () => [] },
  // 录音浮层（以麦克风按钮为锚点向上浮出）
  voiceActive: { type: Boolean, default: false },
  isRecording: { type: Boolean, default: false },
  partialText: { type: String, default: '' },
  recordSeconds: { type: Number, default: 0 }
})

const emit = defineEmits([
  'update:inputText',
  'send',
  'cancel',
  'image-change',
  'remove-image',
  'toggle-thinking',
  'mention-agent',
  'clear-mention',
  'select-all-kbs',
  'toggle-kb',
  'voice-toggle',
  'update:settings-visible',
  'update:kws-enabled',
  'update:kws-keywords',
  'update:tts-engine',
  'update:tts-voice',
  'save-kws',
  'cancel-record',
  'voice-finish',
  'voice-start-input'
])

// ==================== 输入框 DOM ====================
const chatInputRef = ref(null)
const imageInputRef = ref(null)

// 输入框自动调节高度（发送后 inputText 清空时同步重置）
function autoResize() {
  const el = chatInputRef.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = Math.min(el.scrollHeight, 260) + 'px'
}
watch(
  () => props.inputText,
  () => { nextTick(() => autoResize()) },
  { immediate: true }
)

// ==================== @ 智能体选择器（UI 交互，数据来自 props） ====================
const atPickerVisible = ref(false)
const atPickerKeyword = ref('')
const atPickerIndex = ref(0)
const atPickerRef = ref(null)
let mentionStartPos = -1 // @ 在 textarea 中的起始位置（非响应式）

// @ 选择器过滤后的智能体列表（按名称/简介模糊匹配）
const atFilteredAgents = computed(() => {
  const kw = atPickerKeyword.value.trim().toLowerCase()
  if (!kw) return props.agentList
  return props.agentList.filter(a =>
    (a.agentName || '').toLowerCase().includes(kw) || (a.agentDesc || '').toLowerCase().includes(kw)
  )
})

// 是否系统智能体：agent_type=system，或 code 以 sys_ 前缀兜底
function isSystemAgent(agent) {
  return agent && (agent.agentType === 'system' || String(agent.agentCode || '').startsWith('sys_'))
}

// @ 选择器：按「业务 / 系统」分组展示
const businessAgents = computed(() => atFilteredAgents.value.filter(a => !isSystemAgent(a)))
const systemAgents = computed(() => atFilteredAgents.value.filter(a => isSystemAgent(a)))

// @ 选择器：分组后的扁平顺序（业务在前，系统在后），键盘上下选择用
const atGroupedAgents = computed(() => [...businessAgents.value, ...systemAgents.value])

// 输入框输入事件：自动调节高度 + @ 选择器实时过滤
function handleInput(e) {
  emit('update:inputText', e.target.value)
  autoResize()
  // @ 选择器打开时：以 @ 到光标间的文本作为过滤关键词
  if (atPickerVisible.value && mentionStartPos >= 0) {
    const ta = chatInputRef.value
    if (ta) {
      atPickerKeyword.value = props.inputText.substring(mentionStartPos, ta.selectionStart).replace(/^@/, '')
      atPickerIndex.value = 0
    }
  }
}

// 输入框键盘事件：@ 唤起智能体选择器；Enter 发送，Alt+Enter 换行
function handleInputKeydown(e) {
  // @ 键：记录起始位置并唤起智能体选择器（@ 正常插入文本，选中后替换）
  if (e.key === '@' && !e.ctrlKey && !e.metaKey && !e.altKey) {
    const ta = chatInputRef.value
    mentionStartPos = ta ? ta.selectionStart : -1
    atPickerKeyword.value = ''
    atPickerIndex.value = 0
    atPickerVisible.value = props.agentList.length > 0
    return
  }

  // @ 选择器打开时的按键导航（按 业务→系统 分组后的扁平顺序）
  if (atPickerVisible.value) {
    const list = atGroupedAgents.value
    const len = list.length
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      atPickerIndex.value = len > 0 ? (atPickerIndex.value + 1) % len : 0
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      atPickerIndex.value = len > 0 ? (atPickerIndex.value - 1 + len) % len : 0
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      const agent = list[Math.min(atPickerIndex.value, len - 1)]
      if (agent) confirmAtPick(agent)
      return
    }
    if (e.key === 'Escape') {
      closeAtPicker()
      return
    }
    // 退格删回 @ 起始位置则关闭选择器；其余按键放行（继续输入过滤）
    if (e.key === 'Backspace') {
      const ta = chatInputRef.value
      if (ta && ta.selectionStart <= mentionStartPos) {
        closeAtPicker()
        return
      }
    }
    return
  }

  // 只处理 Enter 键，其他所有按键（Ctrl+V 粘贴等）完全放行
  if (e.key !== 'Enter' || e.repeat) return

  if (e.altKey) {
    // Alt+Enter：阻止浏览器插入换行，手动在光标位置插入
    e.preventDefault()
    const textarea = chatInputRef.value
    if (!textarea) return
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    emit('update:inputText', props.inputText.substring(0, start) + '\n' + props.inputText.substring(end))
    nextTick(() => {
      textarea.selectionStart = textarea.selectionEnd = start + 1
      autoResize()
    })
  } else if (!e.ctrlKey && !e.metaKey) {
    // 纯 Enter：发送消息，阻止插入换行
    e.preventDefault()
    emit('send')
  }
}

// @ 选择器：确认选中某个智能体（替换文本中的 @，默认全选其知识库）
function confirmAtPick(agent) {
  if (!agent) return
  const ta = chatInputRef.value
  const end = ta ? ta.selectionEnd : props.inputText.length
  const start = mentionStartPos >= 0 ? mentionStartPos : end
  // 移除 textarea 中 @ 及之后的过滤词
  emit('update:inputText', props.inputText.substring(0, start) + props.inputText.substring(end))
  // 设置提及智能体（页面负责 mentionedAgent + contextKbs 联动），先关闭选择器
  emit('mention-agent', agent.agentCode)
  atPickerVisible.value = false
  mentionStartPos = -1
  nextTick(() => {
    autoResize()
    if (ta) { ta.focus(); ta.selectionStart = ta.selectionEnd = start }
  })
}

// 点击上下文栏智能体 chip：直接唤起 @ 选择器切换智能体
function openAtPickerFromChip() {
  if (props.agentList.length === 0) return
  kbPickerVisible.value = false
  mentionStartPos = -1
  atPickerKeyword.value = ''
  atPickerIndex.value = 0
  atPickerVisible.value = true
}

// @ 选择器：关闭
function closeAtPicker() {
  atPickerVisible.value = false
  mentionStartPos = -1
}

// 点击外部关闭 @ 选择器
function handleAtClickOutside(e) {
  if (atPickerVisible.value && atPickerRef.value && !atPickerRef.value.contains(e.target) && !e.target.closest('.chat-input-card')) {
    closeAtPicker()
  }
}

// 点击外部关闭语音设置弹窗（点击自身作用域内不关闭）
function handleVoiceSettingClickOutside(e) {
  if (!props.settingsVisible) return
  if (e.target.closest('.voice-setting-wrap')) return
  emit('update:settings-visible', false)
}

// ==================== 知识库选择弹窗（点击入口 chip 弹出） ====================
const kbPickerVisible = ref(false)

// 打开知识库选择弹窗（与 @ 智能体选择器互斥）
function openKbPicker() {
  if (props.effectiveKnowledges.length === 0) return
  closeAtPicker()
  kbPickerVisible.value = true
}

function closeKbPicker() {
  kbPickerVisible.value = false
}

// 点击外部关闭知识库选择弹窗
function handleKbClickOutside(e) {
  if (kbPickerVisible.value && !e.target.closest('.kb-picker') && !e.target.closest('.kb-chip')) {
    kbPickerVisible.value = false
  }
}

// @ 选择器搜索框键盘事件：复用上下选择 / Enter 确认 / Esc 关闭
function handleAtSearchKeydown(e) {
  const list = atGroupedAgents.value
  const len = list.length
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    atPickerIndex.value = len > 0 ? (atPickerIndex.value + 1) % len : 0
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    atPickerIndex.value = len > 0 ? (atPickerIndex.value - 1 + len) % len : 0
  } else if (e.key === 'Enter') {
    e.preventDefault()
    const agent = list[Math.min(atPickerIndex.value, len - 1)]
    if (agent) confirmAtPick(agent)
  } else if (e.key === 'Escape') {
    closeAtPicker()
  }
}

// 图片选择：读取 File 交给父级（useChatStream 校验/预览）
function handleImageChange(e) {
  const file = e.target.files && e.target.files[0]
  if (file) emit('image-change', file)
  e.target.value = ''
}

onMounted(() => {
  document.addEventListener('click', handleAtClickOutside)
  document.addEventListener('click', handleVoiceSettingClickOutside)
  document.addEventListener('click', handleKbClickOutside)
})
onUnmounted(() => {
  document.removeEventListener('click', handleAtClickOutside)
  document.removeEventListener('click', handleVoiceSettingClickOutside)
  document.removeEventListener('click', handleKbClickOutside)
})
</script>

<style scoped>
.chat-input-wrap {
  position: relative;
  margin: 0 20px 16px;
  flex-shrink: 0;
}

.chat-input-card {
  position: relative;
  background: linear-gradient(180deg, #fcfdfe 0%, #ffffff 42%);
  border: 1px solid #e6eaf0;
  border-radius: 16px;
  overflow: visible;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03), 0 6px 18px rgba(15, 23, 42, 0.05);
  transition: border-color 0.22s ease, box-shadow 0.22s ease, background 0.22s ease;
}

.chat-input-card:hover {
  border-color: #dbe2ea;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 22px rgba(15, 23, 42, 0.07);
}

.chat-input-card:focus-within {
  background: #fff;
  border-color: #a3cdff;
  box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.1), 0 8px 24px rgba(64, 158, 255, 0.1);
}

.chat-input-body {
  display: flex;
  flex-direction: column;
}

.chat-input-textarea {
  display: block;
  width: 100%;
  min-height: 52px;
  max-height: 260px;
  padding: 16px 20px 6px;
  /* 上传图片时顶部留出空间给预览图，避免文字被遮挡 */
  /* padding-top 动态由内联样式覆盖 */
  border: none;
  outline: none;
  resize: none;
  font-size: 15px;
  line-height: 1.65;
  color: #1f2328;
  background: transparent;
  font-family: inherit;
  caret-color: #409eff;
}

/* 输入区细滚动条，超长文本时不突兀 */
.chat-input-textarea::-webkit-scrollbar {
  width: 6px;
}
.chat-input-textarea::-webkit-scrollbar-thumb {
  background: #e4e7ed;
  border-radius: 4px;
}
.chat-input-textarea::-webkit-scrollbar-thumb:hover {
  background: #d0d7de;
}

.chat-input-textarea::placeholder {
  color: #a8b1bb;
}

.chat-input-counter {
  text-align: right;
  font-size: 11.5px;
  color: #b8c0c8;
  padding: 0 18px 2px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

/* 图片预览：作为独立行排在 textarea 之上，自然占高度（参考豆包：无图时卡片不增加高度） */
.chat-input-image-preview {
  padding: 10px 14px 0;
}

.preview-thumb {
  position: relative;
  width: 56px;
  height: 56px;
  border-radius: 6px;
  overflow: hidden;
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.preview-thumb img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-remove-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 1;
  padding: 0;
  transition: background 0.15s;
}

.image-remove-btn:hover {
  background: rgba(0, 0, 0, 0.85);
}

/* ===== @ 智能体选择器 ===== */
.at-picker {
  position: absolute;
  left: 0;
  bottom: 100%;
  z-index: 30;
  width: min(520px, 100%);
  margin-bottom: 8px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  animation: at-picker-in 0.16s ease-out;
}
@keyframes at-picker-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}
.at-picker-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px 8px;
}
.at-picker-close {
  margin-left: 8px;
}
.at-picker-title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2328;
}
.at-picker-count {
  font-size: 12px;
  color: #8b949e;
}
.at-picker-search {
  padding: 0 12px 8px;
}
.at-picker-search .el-input__wrapper {
  background: #f5f7fa;
  box-shadow: none;
  border-radius: 9px;
}
.at-picker-search .el-input__wrapper.is-focus {
  background: #fff;
  box-shadow: 0 0 0 1px #409eff inset;
}
.at-picker-list {
  max-height: 300px;
  overflow-y: auto;
  padding: 4px;
}
.at-picker-group-label {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1px;
  color: #8b949e;
}
.at-picker-group-label::before {
  content: '';
  width: 3px;
  height: 12px;
  border-radius: 2px;
  background: currentColor;
  opacity: 0.8;
}
.at-picker-group-label.business {
  color: #409eff;
}
.at-picker-group-label.system {
  color: #8b5cf6;
}
.at-picker-divider {
  height: 1px;
  margin: 6px 10px 2px;
  background: linear-gradient(90deg, #f0f2f5, #e4e7ed, #f0f2f5);
}
.at-picker-type {
  flex-shrink: 0;
  font-size: 11px;
  line-height: 1;
  padding: 3px 7px;
  border-radius: 999px;
  white-space: nowrap;
  font-weight: 500;
}
.at-picker-type.business {
  color: #409eff;
  background: rgba(64, 158, 255, 0.1);
}
.at-picker-type.system {
  color: #8b5cf6;
  background: rgba(139, 92, 246, 0.1);
}
.at-picker-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s;
}
.at-picker-item:hover {
  background: #f6f8fa;
}
.at-picker-item.active {
  background: #ecf5ff;
}
.at-picker-item.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 22px;
  border-radius: 2px;
  background: #409eff;
}
.at-picker-item .el-avatar {
  box-shadow: 0 0 0 2px #fff, 0 0 0 3px #e4e7ed;
}
.at-picker-item.active .el-avatar {
  box-shadow: 0 0 0 2px #fff, 0 0 0 3px #409eff;
}
/* 系统智能体选中态：紫色主题，与业务智能体区分 */
.at-picker-item.system.active {
  background: rgba(139, 92, 246, 0.08);
}
.at-picker-item.system.active::before {
  background: #8b5cf6;
}
.at-picker-item.system.active .el-avatar {
  box-shadow: 0 0 0 2px #fff, 0 0 0 3px #8b5cf6;
}
.at-picker-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.at-picker-name {
  font-size: 14px;
  color: #1f2328;
  font-weight: 600;
  line-height: 1.3;
}
.at-picker-desc {
  font-size: 12px;
  color: #8b949e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}
.at-picker-kbcount {
  flex-shrink: 0;
  font-size: 12px;
  color: #57606a;
  background: #f0f2f5;
  border-radius: 999px;
  padding: 3px 9px;
  white-space: nowrap;
}
.at-picker-item.active .at-picker-kbcount {
  color: #409eff;
  background: rgba(64, 158, 255, 0.12);
}
.at-picker-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 30px 0;
  color: #8b949e;
  font-size: 13px;
}
.at-picker-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  border-top: 1px solid #f0f2f5;
  font-size: 12px;
  color: #8b949e;
}
.at-picker-footer .el-text {
  margin: 0 2px;
}

/* ===== 智能体上下文栏（@ 提及 + 知识库入口，知识库选择收进弹窗）===== */
.agent-context-bar {
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 10px 14px;
  margin: 0 4px;
  border-bottom: 1px solid #f2f4f7;
  position: relative;
}
/* 智能体行：标签 + 智能体 chip + 知识库入口 chip 强制一行，名称超长时截断 */
.agent-chip-row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px 6px;
  min-width: 0;
}
.mention-agent-label {
  flex-shrink: 0;
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0.3px;
  color: #a0a8b0;
}
.mention-agent-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 1 auto;
  min-width: 0;
  height: 28px;
  padding: 0 10px 0 8px;
  border: 1px solid #e4e7ed;
  border-radius: 999px;
  background: #fff;
  color: #57606a;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.18s;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.mention-agent-chip:hover {
  border-color: #409eff;
  color: #409eff;
  box-shadow: 0 2px 6px rgba(64, 158, 255, 0.14);
}
.mention-agent-chip .agent-avatar {
  flex-shrink: 0;
}
/* 系统智能体 chip：紫色主题，与业务智能体区分 */
.mention-agent-chip.is-system {
  border-color: #c4b5fd;
  color: #8b5cf6;
}
.mention-agent-chip.is-system:hover {
  border-color: #8b5cf6;
  box-shadow: 0 2px 6px rgba(139, 92, 246, 0.14);
}
/* @ 与 18px 头像同高居中，弱化字重让两者融为一体 */
.mention-at {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 13px;
  height: 18px;
  margin-right: -2px;
  font-size: 13px;
  font-weight: 500;
  color: #a8b1bb;
  line-height: 1;
}
.mention-agent-chip:hover .mention-at {
  color: inherit;
}
.mention-agent-name {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
}
.mention-agent-switch {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
  margin-left: 2px;
  padding-left: 8px;
  border-left: 1px solid #ebeef5;
  font-size: 12px;
  color: #8b949e;
}
/* 知识库入口 chip：显示当前选择状态，点击弹出选择面板 */
.kb-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  height: 28px;
  padding: 0 10px;
  border: 1px solid #e4e7ed;
  border-radius: 999px;
  background: #fff;
  color: #57606a;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.18s;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  user-select: none;
}
.kb-chip:hover {
  border-color: #409eff;
  color: #409eff;
  box-shadow: 0 2px 6px rgba(64, 158, 255, 0.14);
}
.kb-chip-status {
  font-weight: 500;
  white-space: nowrap;
}
.kb-chip-switch {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
  margin-left: 2px;
  padding-left: 8px;
  border-left: 1px solid #ebeef5;
  font-size: 12px;
  color: #8b949e;
}

/* 知识库选择弹窗（浮层，从输入卡片顶部向上浮出） */
.kb-picker {
  position: absolute;
  left: 0;
  bottom: calc(100% + 8px);
  z-index: 30;
  width: min(360px, 100%);
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  animation: at-picker-in 0.16s ease-out;
}
.kb-picker-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px 8px;
}
.kb-picker-title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2328;
}
.kb-picker-close {
  border: none;
  background: transparent;
  font-size: 18px;
  line-height: 1;
  padding: 2px;
  color: #8b949e;
  cursor: pointer;
}
.kb-picker-close:hover {
  color: #1f2328;
}
.kb-picker-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 260px;
  overflow-y: auto;
  padding: 4px 8px 10px;
}
.kb-picker-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s;
}
.kb-picker-item:hover {
  background: #f6f8fa;
}
.kb-picker-item.all.active {
  background: #ecf5ff;
}
.kb-picker-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 1px solid #d0d7de;
  border-radius: 4px;
  background: #fff;
  color: #fff;
  flex-shrink: 0;
  transition: all 0.15s;
}
.kb-picker-check.active {
  background: #409eff;
  border-color: #409eff;
}
.kb-picker-name {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
  color: #1f2328;
}
.kb-picker-item.all .kb-picker-name {
  font-weight: 600;
}
.kb-picker-desc {
  flex-shrink: 0;
  font-size: 11.5px;
  color: #a0a8b0;
}

.chat-input-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 12px 10px;
}

.chat-input-tools {
  display: flex;
  align-items: center;
  gap: 8px;
}

.chat-tool-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 13px;
  border: 1px solid #e9edf2;
  border-radius: 999px;
  background: #fbfcfd;
  color: #656d76;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  line-height: 1;
}

.chat-tool-chip:hover {
  background: #f2f5f8;
  border-color: #d8dfe6;
  color: #46505a;
}

.chat-tool-chip:active {
  transform: scale(0.97);
}

/* 语音设置 chip 上的唤醒监听红点 */
.tool-kws-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #f56c6c;
  flex-shrink: 0;
  animation: tool-kws-blink 1.2s ease-in-out infinite;
}
@keyframes tool-kws-blink {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.8); }
}

/* ===== 语音设置小弹窗（按钮上方，自控悬浮层） ===== */
.voice-setting-wrap {
  position: relative;
  display: inline-flex;
}
.voice-setting-pop {
  position: absolute;
  left: 0;
  bottom: 100%;
  z-index: 40;
  /* 自适应宽度：优先 400px，窄屏跟随视口收缩，避免溢出 */
  width: 400px;
  max-width: calc(100vw - 48px);
  /* 高度受限时内部滚动，箭头不随滚动 */
  max-height: min(60vh, 520px);
  overflow-y: auto;
  overscroll-behavior: contain;
  margin-bottom: 10px;
  padding: 16px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: vsp-in 0.16s ease-out;
}
@keyframes vsp-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}
/* 小箭头指向语音设置按钮（位于 wrap 内，与弹窗底边对齐） */
.vsp-arrow {
  position: absolute;
  left: 22px;
  bottom: calc(100% + 5px);
  z-index: 41;
  width: 9px;
  height: 9px;
  background: #fff;
  border-right: 1px solid #e4e7ed;
  border-bottom: 1px solid #e4e7ed;
  transform: rotate(45deg);
}
.vsp-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
/* 右侧控件区：不被压缩，并给出合理最小宽度 */
.vsp-row > .el-switch,
.vsp-row > .el-radio-group,
.vsp-row > .el-input,
.vsp-row > .el-select {
  flex-shrink: 0;
}
.vsp-row > .el-input,
.vsp-row > .el-select {
  width: 200px;
}
.vsp-row.column {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}
/* 纵向排列时控件占满整行 */
.vsp-row.column > .el-input,
.vsp-row.column > .el-select {
  width: 100%;
}
.vsp-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1;
}
.vsp-label {
  font-size: 13px;
  font-weight: 600;
  color: #1f2328;
}
.vsp-desc {
  font-size: 12px;
  color: #8b949e;
  line-height: 1.5;
}
.vsp-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f2f5;
}

/* 窄屏（弹窗宽度不足）：标签与控件改为上下排列，控件占满 */
@media (max-width: 640px) {
  .voice-setting-pop {
    width: calc(100vw - 48px);
    padding: 14px;
    gap: 12px;
  }
  .vsp-row {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  .vsp-row > .el-switch,
  .vsp-row > .el-radio-group {
    align-self: flex-start;
  }
  .vsp-row > .el-input,
  .vsp-row > .el-select {
    width: 100%;
  }
}

.chat-tool-chip.active {
  background: linear-gradient(180deg, #f0f7ff, #e9f3ff);
  border-color: #b6d9ff;
  color: #2b7fd4;
  box-shadow: 0 1px 3px rgba(64, 158, 255, 0.12);
}

.chat-tool-chip.active:hover {
  background: linear-gradient(180deg, #eaf4ff, #ddedff);
  border-color: #92c8ff;
  color: #2b7fd4;
}

.chat-tool-chip.forced {
  cursor: default;
}

.chat-tool-chip.forced:hover {
  background: #ecf5ff;
  border-color: #409eff;
}

.chat-input-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 语音输入按钮锚点：录音浮层以此为基准向上浮出 */
.voice-input-wrap {
  position: relative;
  display: inline-flex;
}

.chat-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #9aa3ac;
  cursor: pointer;
  transition: all 0.16s ease;
}

.chat-action-btn:hover {
  background: #f1f4f8;
  color: #409eff;
}

.chat-action-btn:active {
  transform: scale(0.94);
}

.chat-action-btn.is-recording {
  background: #fef0f0;
  color: #f56c6c;
}

/* 发送按钮与操作按钮间加一道细分隔，视觉上区分主次 */
.chat-send-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  margin-left: 6px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(135deg, #56a8ff, #2f7fe6);
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(47, 127, 230, 0.28);
}

.chat-send-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #3f99ff, #2270d8);
  transform: translateY(-1px) scale(1.04);
  box-shadow: 0 4px 12px rgba(47, 127, 230, 0.34);
}

.chat-send-btn:active:not(:disabled) {
  transform: scale(0.96);
  box-shadow: 0 1px 4px rgba(47, 127, 230, 0.28);
}

.chat-send-btn:disabled {
  background: #e3e8ee;
  color: #b4bcc4;
  box-shadow: none;
  cursor: not-allowed;
}

.chat-send-btn.cancel {
  background: linear-gradient(135deg, #f06b76, #d92d3c);
  box-shadow: 0 2px 6px rgba(217, 45, 60, 0.28);
}

.chat-send-btn.cancel:hover {
  background: linear-gradient(135deg, #e2515e, #b81f2c);
  box-shadow: 0 4px 12px rgba(217, 45, 60, 0.34);
}
</style>
